import { api } from './client'

import type { NoteStatus } from './sorting-lot-api'

export type PurchaseLotInReason =
    | 'from_purchase'
    | 'return_from_sorting'
    | 'adjustment'
export type PurchaseLotOutReason = 'for_sorting' | 'discard' | 'adjustment'

export interface PurchaseLot {
    id: string
    code: string
    tea_purchase_id: string
    purchase_code?: string
    grower_name?: string
    quantity_in: number
    quantity_available: number
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
}

export interface PurchaseLotStockInNoteItem {
    id: string
    note_id: string
    purchase_lot_id: string
    tea_purchase_id: string
    purchase_lot_code?: string
    purchase_code?: string
    grower_name?: string
    quantity: number
    created_at: string
}

export interface PurchaseLotStockInNote {
    id: string
    code: string
    status: NoteStatus
    reason: PurchaseLotInReason
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    approved_by_id?: string | null
    approved_by_name?: string
    approved_at?: string | null
    rejected_reason?: string | null
    cancelled_at?: string | null
    stocktake_session_id?: string | null
    created_at: string
    updated_at: string
    items_count?: number
    items?: PurchaseLotStockInNoteItem[]
}

export interface PurchaseLotStockOutNote extends Omit<
    PurchaseLotStockInNote,
    'reason' | 'items'
> {
    reason: PurchaseLotOutReason
    items?: PurchaseLotStockOutNoteItem[]
    /** PXK đã bị một PNK phân loại sống link (chỉ có ở API detail). */
    has_active_in_note?: boolean
}

export type PurchaseLotStockOutNoteItem = PurchaseLotStockInNoteItem

// ============================================================================
// Phiếu lấy hàng (withdrawal note) — phiếu độc lập, không đụng tồn kho.
// Vòng đời: open|closed|cancelled. Không có bước duyệt.
// ============================================================================

export type WithdrawalNoteStatus = 'open' | 'closed' | 'cancelled'

export interface PurchaseLotWithdrawalNoteItem {
    id: string
    note_id: string
    purchase_lot_id: string
    purchase_lot_code?: string
    purchase_code?: string
    grower_name?: string
    quantity: number
    created_at: string
}

export interface PurchaseLotWithdrawalNote {
    id: string
    code: string
    status: WithdrawalNoteStatus
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
    items_count?: number
    items?: PurchaseLotWithdrawalNoteItem[]
}

export interface CreateWithdrawalNoteItemInput {
    purchase_lot_id: string
    quantity: number
}

export interface CreateWithdrawalNoteRequest {
    notes?: string
    items: CreateWithdrawalNoteItemInput[]
}

export interface ListWithdrawalNotesParams {
    status?: WithdrawalNoteStatus
    search?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export interface PurchaseLotHistoryEntry {
    note_id: string
    note_code: string
    note_type: 'in' | 'out'
    status: NoteStatus
    reason: string
    quantity: number
    notes?: string | null
    created_at: string
    approved_at?: string | null
    created_by_name?: string
}

/**
 * Item của PNK lô chè mua: đúng 1 nhánh — `purchase_lot_id` (lô có sẵn) HOẶC
 * `tea_purchase_id` (lô mới). Lô mới BẮT BUỘC gắn đơn mua vì
 * purchase_lots.tea_purchase_id là NOT NULL, và chỉ hợp lệ với reason=adjustment.
 */
export type CreatePurchaseInNoteItem =
    | { purchase_lot_id: string; quantity: number }
    | { tea_purchase_id: string; new_lot_code?: string; quantity: number }

export interface CreatePurchaseInNoteRequest {
    reason: PurchaseLotInReason
    tea_purchase_id?: string
    notes?: string
    items?: CreatePurchaseInNoteItem[]
}

/**
 * Sửa PNK lô chè mua đang pending. `reason` bắt buộc trùng phiếu gốc (BE chặn
 * đổi loại phiếu). reason=from_purchase chỉ nhận `notes` (items auto-split);
 * các reason khác gửi lại toàn bộ `items`.
 */
export interface UpdatePurchaseInNoteRequest {
    reason: PurchaseLotInReason
    tea_purchase_id?: string
    notes?: string
    items?: CreatePurchaseInNoteItem[]
}

export interface CreatePurchaseOutNoteRequest {
    reason: PurchaseLotOutReason
    notes?: string
    items: Array<{
        purchase_lot_id: string
        quantity: number
    }>
}

/** Sửa PXK lô chè mua đang pending. Reason bất biến; gửi lại toàn bộ item. */
export interface UpdatePurchaseOutNoteRequest {
    reason: PurchaseLotOutReason
    notes?: string
    items: Array<{
        purchase_lot_id: string
        quantity: number
    }>
}

export interface ListPurchaseLotsParams {
    tea_purchase_id?: string
    search?: string
    only_available?: '1' | undefined
    page?: number
    per_page?: number
}

export interface ListPurchaseNotesParams {
    status?: NoteStatus
    reason?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

const BASE = '/purchase-lots'

export const purchaseLotApi = {
    listLots: (params?: ListPurchaseLotsParams) =>
        api.get<PurchaseLot[]>(BASE, { params }),
    getLot: (id: string) => api.get<PurchaseLot>(`${BASE}/${id}`),
    getLotHistory: (
        id: string,
        params?: { page?: number; per_page?: number },
    ) =>
        api.get<PurchaseLotHistoryEntry[]>(`${BASE}/${id}/history`, { params }),

    listInNotes: (params?: ListPurchaseNotesParams) =>
        api.get<PurchaseLotStockInNote[]>(`${BASE}/stock-in-notes`, {
            params,
        }),
    getInNote: (id: string) =>
        api.get<PurchaseLotStockInNote>(`${BASE}/stock-in-notes/${id}`),
    createInNote: (req: CreatePurchaseInNoteRequest) =>
        api.post<PurchaseLotStockInNote>(`${BASE}/stock-in-notes`, req),
    updateInNote: (id: string, req: UpdatePurchaseInNoteRequest) =>
        api.put<PurchaseLotStockInNote>(`${BASE}/stock-in-notes/${id}`, req),
    approveInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/approve`),
    rejectInNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/reject`, { reason }),
    cancelInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/cancel`),

    listOutNotes: (params?: ListPurchaseNotesParams) =>
        api.get<PurchaseLotStockOutNote[]>(`${BASE}/stock-out-notes`, {
            params,
        }),
    /**
     * PXK đã duyệt, lý do "đưa đi phân loại" — picker khi tạo PNK lô phân loại.
     * Không lọc PXK đã dùng (1 PXK sinh bao nhiêu PNK cũng được) nên danh sách
     * chỉ phình ra → BE giới hạn 100 dòng; dùng search (mã phiếu) để thu hẹp.
     */
    listOutNotesForSorting: (search?: string) =>
        api.get<PurchaseLotStockOutNote[]>(
            `${BASE}/stock-out-notes/for-sorting`,
            { params: search ? { search } : undefined },
        ),
    getOutNote: (id: string) =>
        api.get<PurchaseLotStockOutNote>(`${BASE}/stock-out-notes/${id}`),
    createOutNote: (req: CreatePurchaseOutNoteRequest) =>
        api.post<PurchaseLotStockOutNote>(`${BASE}/stock-out-notes`, req),
    updateOutNote: (id: string, req: UpdatePurchaseOutNoteRequest) =>
        api.put<PurchaseLotStockOutNote>(`${BASE}/stock-out-notes/${id}`, req),
    approveOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/approve`),
    rejectOutNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/reject`, { reason }),
    cancelOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/cancel`),

    // Phiếu lấy hàng (withdrawal notes)
    listWithdrawalNotes: (params?: ListWithdrawalNotesParams) =>
        api.get<PurchaseLotWithdrawalNote[]>(`${BASE}/withdrawal-notes`, {
            params,
        }),
    getWithdrawalNote: (id: string) =>
        api.get<PurchaseLotWithdrawalNote>(`${BASE}/withdrawal-notes/${id}`),
    createWithdrawalNote: (req: CreateWithdrawalNoteRequest) =>
        api.post<PurchaseLotWithdrawalNote>(`${BASE}/withdrawal-notes`, req),
    cancelWithdrawalNote: (id: string) =>
        api.post<void>(`${BASE}/withdrawal-notes/${id}/cancel`),
}
